import { HTMLAttributes, useMemo } from "react";
import { cx } from "../../utils/cx";
import type { ProgressSize } from "./Progress";
import styles from "./ProgressCircular.module.scss";

const SIZE_PX: Record<ProgressSize, number> = {
    sm: 28,
    md: 36,
    lg: 44,
};

const STROKE: Record<ProgressSize, number> = {
    sm: 3,
    md: 3.5,
    lg: 4,
};

export interface ProgressCircularProps extends HTMLAttributes<HTMLDivElement> {
    value?: number;
    min?: number;
    max?: number;
    indeterminate?: boolean;
    size?: ProgressSize;
    disabled?: boolean;
}

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
}

/**
 * Treez DS — Progress (circular).
 *
 * Figma **Progress** — ring track (`grey-02`) + arc (`green-10`), with determinate
 * and indeterminate (spinning arc) states.
 */
export function ProgressCircular({
    value = 0,
    min = 0,
    max = 100,
    indeterminate = false,
    size = "md",
    disabled = false,
    className,
    "aria-label": ariaLabel = "Progress",
    ...rest
}: ProgressCircularProps) {
    const px = SIZE_PX[size];
    const stroke = STROKE[size];
    const r = (px - stroke) / 2 - 0.5;
    const c = 2 * Math.PI * r;
    const center = px / 2;

    const ratio = useMemo(() => {
        if (indeterminate || max <= min) return 0;
        return clamp((value - min) / (max - min), 0, 1);
    }, [value, min, max, indeterminate]);

    const roundedValue = Math.round(value);
    const dashOffset = c * (1 - ratio);
    const indeterminateArc = c * 0.28;

    return (
        <div
            className={cx(styles.root, className)}
            data-disabled={disabled || undefined}
            data-indeterminate={indeterminate || undefined}
            data-size={size}
            data-ui="progress-circular"
            {...rest}
        >
            <div
                aria-busy={indeterminate || undefined}
                aria-label={ariaLabel}
                aria-valuemax={indeterminate ? undefined : max}
                aria-valuemin={indeterminate ? undefined : min}
                aria-valuenow={indeterminate ? undefined : roundedValue}
                className={cx(
                    styles.svgWrap,
                    indeterminate
                        ? styles.svgWrapIndeterminate
                        : styles.svgWrapDeterminate,
                )}
                role="progressbar"
            >
                <svg
                    className={styles.svg}
                    height={px}
                    viewBox={`0 0 ${px} ${px}`}
                    width={px}
                >
                    <circle
                        className={styles.track}
                        cx={center}
                        cy={center}
                        r={r}
                        strokeLinecap="butt"
                        strokeWidth={stroke}
                    />
                    <circle
                        className={styles.indicator}
                        cx={center}
                        cy={center}
                        r={r}
                        strokeDasharray={
                            indeterminate
                                ? `${indeterminateArc} ${c}`
                                : `${c} ${c}`
                        }
                        strokeDashoffset={indeterminate ? c * 0.08 : dashOffset}
                        strokeLinecap="butt"
                        strokeWidth={stroke}
                        style={
                            indeterminate
                                ? { transformOrigin: "50% 50%" }
                                : undefined
                        }
                    />
                </svg>
            </div>
        </div>
    );
}

ProgressCircular.displayName = "ProgressCircular";
